/**
 * The campaign's failed collections, seven days back, grouped by the day the collection was due
 * (platform design §6.5; Figma `272:8405`). One row per slot with the reason's own sentence: a
 * failure has no sub-structure, so this stays simpler than the agenda.
 */

import type { ReactNode } from 'react';
import { Fragment, useMemo } from 'react';

import { PageHeader } from '../../../chrome/page';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { Badge } from '../../../ui/badge';
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
} from '../../../ui/item';
import { useFailures, type Window } from './api/queries';
import type { Failure } from './api/schemas';
import { dayHeading, formatTime, startOfDay } from './dayHeading';
import { REASON_ICON, reasonSentence } from './failureReasons';
import { groupFailuresByDay } from './failureGrouping';
import { cm } from './messages';

const LOOKBACK_DAYS = 7;

function useFailuresWindow(now: Date): Window {
  return useMemo(() => {
    const from = startOfDay(now);
    from.setDate(from.getDate() - (LOOKBACK_DAYS - 1));
    return { from: from.toISOString(), to: now.toISOString() };
  }, [now]);
}

function accountName(failure: Failure): string {
  return failure.account_label ?? failure.account_handle ?? failure.target_id;
}

export function Failures(props: {
  campaignId: string;
  /** Pins "now" so a story or a test renders the same day headings on every run. */
  now?: Date;
}): ReactNode {
  const locale = getLocale();
  const now = useMemo(() => props.now ?? new Date(), [props.now]);
  const range = useFailuresWindow(now);
  const failures = useFailures(props.campaignId, range);

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader
        title={cm('failures.page.title')}
        actions={
          failures.data !== undefined && failures.data.length > 0 ? (
            <Badge variant="secondary">
              {cm('failures.page.count', { count: integer(failures.data.length, locale) })}
            </Badge>
          ) : undefined
        }
      />
      <QueryStates
        query={failures}
        empty={<EmptyState title={cm('failures.page.empty.title')} tone="neutral" />}
      >
        {(data) => (
          <div className="flex flex-col gap-6">
            {groupFailuresByDay(data).map((day) => (
              <section key={day.key} className="flex flex-col gap-3">
                <h2 className="text-caption font-medium text-muted-foreground">
                  {dayHeading(day.date, now)}
                </h2>
                <ItemGroup className="overflow-hidden rounded-xl border bg-card">
                  {day.failures.map((failure, index) => {
                    const Icon = REASON_ICON[failure.reason];
                    return (
                      <Fragment
                        key={`${failure.target_id}-${failure.job_type}-${failure.scheduled_slot}`}
                      >
                        {index > 0 && <ItemSeparator />}
                        <Item role="listitem" className="rounded-none">
                          <ItemMedia>
                            <Icon aria-hidden className="size-4 text-muted-foreground" />
                          </ItemMedia>
                          <ItemContent>
                            <ItemTitle>
                              {cm('failures.page.rowTitle', {
                                account: accountName(failure),
                                time: formatTime(new Date(failure.scheduled_slot), locale),
                              })}
                            </ItemTitle>
                            <ItemDescription className="line-clamp-none">
                              {reasonSentence(failure.reason)}
                            </ItemDescription>
                          </ItemContent>
                        </Item>
                      </Fragment>
                    );
                  })}
                </ItemGroup>
              </section>
            ))}
          </div>
        )}
      </QueryStates>
    </div>
  );
}
